import { adminSafeErrorMessage } from "./features/shared-ui/safe-display.js";

export interface AdminApiClientConfig {
  readonly baseUrl: string;
  readonly csrfToken?: string;
  readonly csrfCookieName?: string;
  readonly fetch?: typeof fetch;
}

export type AdminApiErrorKind =
  "auth" | "access" | "not-found" | "conflict" | "validation" | "rate-limit" | "server" | "unknown";

export class AdminApiError extends Error {
  public constructor(
    message: string,
    public readonly kind: AdminApiErrorKind,
    public readonly status: number,
    public readonly correlationId?: string,
  ) {
    super(message);
    this.name = "AdminApiError";
  }
}

export interface AdminApiRequest {
  readonly method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  readonly path: string;
  readonly query?: Record<string, string | number | boolean | undefined>;
  readonly body?: unknown;
  readonly reason?: string;
  readonly confirmation?: string;
}

export interface AdminApiResponse<TData> {
  readonly data: TData;
  readonly meta?: Record<string, unknown>;
}

export class AdminApiClient {
  private readonly fetchImpl: typeof fetch;

  public constructor(private readonly config: AdminApiClientConfig) {
    this.fetchImpl = config.fetch ?? ((input, init) => globalThis.fetch(input, init));
  }

  public async request<TData>(request: AdminApiRequest): Promise<TData> {
    const url = this.toUrl(request);
    writeAdminApiDebugLog("request:start", {
      method: request.method ?? "GET",
      url: withoutQuery(url),
    });
    const response = await this.fetchImpl(url, this.toRequestInit(request));
    writeAdminApiDebugLog("request:response", { status: response.status, url: withoutQuery(url) });
    const payload = await this.parsePayload(response);

    if (!response.ok) {
      throw this.toError(response, payload);
    }

    return this.toData<TData>(payload);
  }

  private toUrl(request: AdminApiRequest): string {
    const url = new URL(request.path, this.config.baseUrl);

    for (const [key, value] of Object.entries(request.query ?? {})) {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }

    return url.toString();
  }

  private toRequestInit(request: AdminApiRequest): RequestInit {
    return {
      method: request.method ?? "GET",
      credentials: "include",
      headers: this.toHeaders(request),
      ...(request.body === undefined ? {} : { body: JSON.stringify(request.body) }),
    };
  }

  private toHeaders(request: AdminApiRequest): HeadersInit {
    const csrfToken = this.csrfTokenForRequest(request);

    return {
      "content-type": "application/json",
      ...(csrfToken === undefined ? {} : { "x-csrf-token": csrfToken }),
      ...(request.reason === undefined ? {} : { "x-admin-reason": request.reason }),
      ...(request.confirmation === undefined
        ? {}
        : { "x-admin-confirmation": request.confirmation }),
      ...(request.method === undefined || request.method === "GET"
        ? {}
        : { "idempotency-key": crypto.randomUUID() }),
    };
  }

  private csrfTokenForRequest(request: AdminApiRequest): string | undefined {
    if (request.method === undefined || request.method === "GET") {
      return undefined;
    }

    return (
      this.config.csrfToken ??
      readBrowserCookie(
        this.config.csrfCookieName ??
          import.meta.env.VITE_ADMIN_CSRF_COOKIE_NAME ??
          "price_rescue_admin_csrf",
      )
    );
  }

  private async parsePayload(response: Response): Promise<unknown> {
    if (response.status === 204) {
      return { data: undefined };
    }

    return response.json() as Promise<unknown>;
  }

  private toData<TData>(payload: unknown): TData {
    if (typeof payload === "object" && payload !== null && "data" in payload) {
      return (payload as AdminApiResponse<TData>).data;
    }

    return payload as TData;
  }

  private toError(response: Response, payload: unknown): AdminApiError {
    return new AdminApiError(
      this.toErrorMessage(payload, response.status),
      this.toErrorKind(response.status),
      response.status,
      this.toCorrelationId(response, payload),
    );
  }

  private toErrorMessage(payload: unknown, status: number): string {
    if (typeof payload === "object" && payload !== null && "error" in payload) {
      const error = (payload as { error?: { message?: string } }).error;
      return adminSafeErrorMessage(error?.message, adminSafeMessage(status));
    }

    return adminSafeMessage(status);
  }

  private toErrorKind(status: number): AdminApiErrorKind {
    if (status === 401) return "auth";
    if (status === 403) return "access";
    if (status === 404) return "not-found";
    if (status === 409) return "conflict";
    if (status === 400 || status === 422) return "validation";
    if (status === 429) return "rate-limit";
    if (status >= 500) return "server";
    return "unknown";
  }

  private toCorrelationId(response: Response, payload: unknown): string | undefined {
    const headerValue = response.headers.get("x-correlation-id");
    if (headerValue !== null && headerValue.length > 0) {
      return headerValue;
    }

    if (typeof payload === "object" && payload !== null && "error" in payload) {
      const error = (payload as { error?: { requestId?: string; correlationId?: string } }).error;
      return error?.requestId ?? error?.correlationId;
    }

    if (typeof payload === "object" && payload !== null && "meta" in payload) {
      const meta = (payload as { meta?: { requestId?: string; correlationId?: string } }).meta;
      return meta?.requestId ?? meta?.correlationId;
    }

    return undefined;
  }
}

function adminSafeMessage(status: number): string {
  if (status === 401) return "Your admin session expired. Sign in again.";
  if (status === 403) return "Your admin role does not allow this operation.";
  if (status === 404) return "The requested admin record was not found.";
  if (status === 409)
    return "This record changed while you were editing. Reload and compare before retrying.";
  if (status === 400 || status === 422) return "Check the highlighted admin form fields.";
  if (status === 429) return "Too many admin requests. Wait briefly and try again.";
  if (status >= 500) return "Admin service is temporarily unavailable.";
  return "Admin API request failed.";
}

function readBrowserCookie(name: string): string | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }

  const rawValue = document.cookie
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${name}=`))
    ?.slice(name.length + 1);

  return rawValue === undefined ? undefined : decodeURIComponent(rawValue);
}

function writeAdminApiDebugLog(event: string, details: Record<string, unknown>): void {
  if (import.meta.env.DEV || import.meta.env.VITE_APP_ENV === "dev") {
    void event;
    void details;
  }
}

function withoutQuery(url: string): string {
  const parsedUrl = new URL(url);
  parsedUrl.search = "";
  return parsedUrl.toString();
}
